import { useState, useEffect } from "react";
import { ClipboardList, Mail, Phone, MapPin } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Input } from "@/components/ui/input";
import { StatusPill, LoadingScreen, EmptyState } from "@/components/common";

export default function AdminLeads() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/requirements").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const term = q.toLowerCase();
  const filtered = (rows || []).filter((r) =>
    [r.customer_name, r.company_name, r.mobile, r.email, r.location, r.security_requirement]
      .some((v) => (v || "").toLowerCase().includes(term)));

  return (
    <DashboardShell menu={adminMenu} title="Customer Leads">
      <PageHeader title="Customer Leads & Requirements" subtitle="Every posted requirement with full customer contact details."
        action={<Input placeholder="Search name, phone, email, city…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" data-testid="lead-search" />} />
      {!rows ? <LoadingScreen /> : rows.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No leads yet" hint="Customer requirements will appear here as they are posted." />
      ) : (
        <div className="bg-white border border-navy-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase">
              <tr>
                <th className="text-left p-3">Customer</th>
                <th className="text-left p-3">Contact</th>
                <th className="text-left p-3">Requirement</th>
                <th className="text-left p-3">Location</th>
                <th className="p-3">Guards</th>
                <th className="p-3">Budget</th>
                <th className="p-3">Quotes</th>
                <th className="p-3">Status</th>
                <th className="p-3">Posted</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-navy-100 align-top" data-testid={`admin-lead-${r.id}`}>
                  <td className="p-3">
                    <div className="font-medium text-navy-900">{r.customer_name}</div>
                    <div className="text-xs text-navy-500">{r.company_name || "Individual"} · {r.property_type || "—"}</div>
                  </td>
                  <td className="p-3">
                    <a href={`tel:${r.mobile}`} className="flex items-center gap-1 text-navy-700 hover:text-brand" data-testid={`lead-phone-${r.id}`}><Phone size={13} /> {r.mobile || "—"}</a>
                    <a href={`mailto:${r.email}`} className="flex items-center gap-1 text-navy-700 hover:text-brand mt-0.5" data-testid={`lead-email-${r.id}`}><Mail size={13} /> {r.email || "—"}</a>
                  </td>
                  <td className="p-3 text-navy-700">{r.security_requirement}<div className="text-xs text-navy-400">{r.shift_duration}h × {r.num_shifts} · {r.contract_duration_months}mo</div></td>
                  <td className="p-3 text-navy-600"><span className="flex items-center gap-1"><MapPin size={13} /> {r.location}</span><div className="text-xs text-navy-400">{r.pincode || ""}</div></td>
                  <td className="p-3 text-center text-navy-700 font-semibold">{r.num_guards}</td>
                  <td className="p-3 text-center text-navy-600 text-xs">{r.budget_range || "—"}</td>
                  <td className="p-3 text-center text-navy-700 font-semibold">{r.quote_count}</td>
                  <td className="p-3 text-center"><StatusPill status={r.status} /></td>
                  <td className="p-3 text-center text-navy-500 text-xs">{r.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
